import React, { useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';

import { useGetEnumsQuery } from '@/api/commonApis';
import CustomDropdown from '@/components/CustomDropdown';
import InputField from '@/components/InputField';
import FormGroupWrapper from '@/features/sampleDevelopment/components/SampleRequestModals/FormGroupWrapper';
import InputWrapper from '@/features/sampleDevelopment/components/SampleRequestModals/InputWrapper';
import {
	formatBrandOptions,
	formatSampleTypeOptions,
	isSampleTypeRequired
} from '@/features/sampleDevelopment/utils';
import { checkAnyUserType, replaceUnderLine } from '@/utils/Util';

const TechpackHandover = ({
	brands,
	requestValues,
	sampleTypeList,
	handleChange,
	errors,
	userList = [],
	handoverModalFor,
	brandMembers = []
}) => {
	const { data: handoverPurposeList = [] } = useGetEnumsQuery({
		path: 'info.nitex.app.product.TPHandoverPurpose'
	});

	const isRequiredDateMandatory = () => {
		return (
			requestValues?.handoverPurpose?.constant === 'SAMPLE_DEVELOPMENT' &&
			checkAnyUserType(['ACCOUNT_MANAGER', 'BUSINESS_DEVELOPMENT_MANAGER'])
		);
	};

	const isAM = handoverModalFor === 'TP_HANDOVER_TO_ACCOUNT_MANAGER';
	const isBdDesigner = handoverModalFor === 'TP_HANDOVER_TO_DESIGNER';
	const isProjectTeam = handoverModalFor === 'TP_HANDOVER_TO_PROJECT_TEAM';
	const isPatternSection = handoverModalFor === 'TP_HANDOVER_TO_PATTERN_SECTION';

	const getHandoverPurpose = () => {
		if (isAM) {
			return handoverPurposeList?.filter((item) =>
				['SAMPLE_DEVELOPMENT', 'ORDER_TP_HANDOVER_FOR_PRODUCTION'].includes(item?.constant)
			);
		}

		if (isBdDesigner) {
			return handoverPurposeList
				?.filter((item) => ['COMPLETE_TP'].includes(item?.constant))
				.map((item) => ({
					...item,
					id: item?.constant,
					name: replaceUnderLine(item?.constant),
					value: replaceUnderLine(item?.constant)
				}));
		}

		if (isProjectTeam) {
			return handoverPurposeList
				?.filter((item) => ['SAMPLE_DEVELOPMENT'].includes(item?.constant))
				.map((item) => ({
					...item,
					id: item?.constant,
					name: replaceUnderLine(item?.constant),
					value: replaceUnderLine(item?.constant)
				}));
		}

		if (isPatternSection) {
			return handoverPurposeList
				?.filter((item) => ['HANDOVER_TO_PATTERN_SECTION'].includes(item?.constant))
				.map((item) => ({
					...item,
					id: item?.constant,
					name: replaceUnderLine(item?.constant),
					value: replaceUnderLine(item?.constant)
				}));
		}

		return handoverPurposeList;
	};

	// Auto fill first index if is are only one index
	useEffect(() => {
		if (getHandoverPurpose()?.length === 1) {
			handleChange('handoverPurpose', getHandoverPurpose()[0]);
		}
	}, [getHandoverPurpose()?.length]);

	// Auto-fill "Handover to" with users assigned to the selected brand
	useEffect(() => {
		const selectedBrandId = requestValues?.brand?.id;
		if (!selectedBrandId) return;

		if (isProjectTeam) {
			// For TP_HANDOVER_TO_PROJECT_TEAM use brand members fetched from the dedicated API
			const mapped = brandMembers.map((user) => ({
				...user,
				value: user?.name,
				label: user?.name
			}));
			handleChange('handoverToPerson', mapped);
			return;
		}

		const matched = userList
			?.filter((user) => user?.brandResponse?.id === selectedBrandId)
			.map((user) => ({ ...user, value: user?.name, label: user?.name }));

		handleChange('handoverToPerson', matched?.length ? matched : []);
	}, [requestValues?.brand?.id, brandMembers]);

	return (
		<React.Fragment>
			<div className="flied-item d-grid grid-columns-3 gap-3">
				<FormGroupWrapper labelTitle={`Customer Name`}>
					<CustomDropdown
						title={`Select brand name`}
						onItemClick={(value) => handleChange('brand', value)}
						selectedItem={requestValues?.brand?.name}
						items={formatBrandOptions(brands)}
					/>
					{/* {errors?.brand && <p className="error">{errors?.brand}</p>} */}
				</FormGroupWrapper>
				<FormGroupWrapper labelTitle="Handover purpose*">
					<CustomDropdown
						title="Select purpose"
						onItemClick={(value) => handleChange('handoverPurpose', value)}
						selectedItem={requestValues?.handoverPurpose?.name}
						items={getHandoverPurpose()?.map((item) => ({
							...item,
							id: item?.constant,
							value: replaceUnderLine(item?.constant),
							name: replaceUnderLine(item?.constant)
						}))}
					/>
					{errors?.handoverPurpose && <p className="error">{errors?.handoverPurpose}</p>}
				</FormGroupWrapper>
				<FormGroupWrapper
					labelTitle={`Sample type${isSampleTypeRequired(requestValues?.handoverPurpose?.constant) ? '*' : ''}`}
				>
					<CustomDropdown
						title="Select sample type"
						onItemClick={(value) => handleChange('sampleType', value)}
						selectedItem={requestValues?.sampleType?.value}
						items={formatSampleTypeOptions(sampleTypeList)}
					/>
					{errors?.sampleType && <p className="error">{errors?.sampleType}</p>}
				</FormGroupWrapper>
			</div>
			<div className="flied-item d-grid grid-columns-4 gap-3">
				<FormGroupWrapper labelTitle={`Handover to*`} reactSelectClass="no-shadow-react-select">
					<CreatableSelect
						placeholder="Select person"
						onChange={(value) => handleChange('handoverToPerson', value)}
						value={requestValues?.handoverToPerson}
						options={userList?.map((user) => ({
							...user,
							value: user?.name,
							label: user?.name
						}))}
						isMulti
						isValidNewOption={() => false}
					/>

					{errors?.handoverToPerson && <p className="error">{errors?.handoverToPerson}</p>}
				</FormGroupWrapper>
				<InputWrapper
					labelTitle={`Send to buyer required date${isRequiredDateMandatory() ? '*' : ''}`}
				>
					<InputField
						type="date"
						name="requiredDate"
						value={requestValues?.requiredDate}
						onChangeHandler={(e) => handleChange('requiredDate', e.target.value)}
						withWrapper={false}
						withLabel={false}
						errors={errors?.requiredDate}
					/>
				</InputWrapper>
				<InputWrapper labelTitle={`Expected order date${isRequiredDateMandatory() ? '*' : ''}`}>
					<InputField
						type="date"
						name="expectedOrderDate"
						value={requestValues?.expectedOrderDate}
						onChangeHandler={(e) => handleChange('expectedOrderDate', e.target.value)}
						withWrapper={false}
						withLabel={false}
						errors={errors?.expectedOrderDate}
					/>
				</InputWrapper>
				<InputWrapper labelTitle="Notes">
					<InputField
						type="text"
						name="note"
						placeholder="Notes"
						withWrapper={false}
						withLabel={false}
						value={requestValues?.note}
						onChangeHandler={(e) => handleChange('note', e.target.value)}
					/>
				</InputWrapper>
			</div>
		</React.Fragment>
	);
};

export default TechpackHandover;
